interface ToastrOptions {
    tapToDismiss: boolean;
    toastClass: string;
    containerId: string;
    showDuration: number;
    hideDuration: number;
    timeOut: number;
    extendedTimeOut: number;
    closeOnHover: boolean;
    iconClasses: Record<string, string>;
    iconClass: string;
    positionClass: string;
    titleClass: string;
    messageClass: string;
    escapeHtml: boolean;
    target: string;
    closeHtml: string;
    closeClass: string;
    closeButton?: boolean;
    newestOnTop: boolean;
    preventDuplicates: boolean;
    progressBar: boolean;
    progressClass: string;
    rtl: boolean;
    onclick?: ((event: Event) => void) | null;
}

let container: HTMLElement | null = null;
let previousToast = "";

const defaults: ToastrOptions = {
    tapToDismiss: true,
    toastClass: "toast",
    containerId: "toast-container",
    showDuration: 300,
    hideDuration: 1000,
    timeOut: 5000,
    extendedTimeOut: 1000,
    closeOnHover: true,
    iconClasses: {
        error: "toast-error",
        info: "toast-info",
        success: "toast-success",
        warning: "toast-warning",
    },
    iconClass: "toast-info",
    positionClass: "toast-top-right",
    titleClass: "toast-title",
    messageClass: "toast-message",
    escapeHtml: false,
    target: "body",
    closeHtml: '<button type="button">&times;</button>',
    closeClass: "toast-close-button",
    newestOnTop: true,
    preventDuplicates: false,
    progressBar: false,
    progressClass: "toast-progress",
    rtl: false,
};

function getOptions(): ToastrOptions {
    return { ...defaults, ...toastr.options };
}

function getContainer(options: ToastrOptions, create = false): HTMLElement | null {
    container = document.getElementById(options.containerId);
    if (container) {
        container.className = options.positionClass;
        return container;
    }
    if (create) {
        container = document.createElement("div");
        container.id = options.containerId;
        container.className = options.positionClass;
        const target = document.querySelector(options.target) ?? document.body;
        target.appendChild(container);
    }
    return container;
}

function escapeHtmlString(source: string): string {
    return source
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

function removeToast(toastElement: HTMLElement): void {
    toastElement.remove();
    if (container && container.children.length === 0) {
        previousToast = "";
    }
}

function fadeIn(element: HTMLElement, duration: number): void {
    element.style.opacity = "0";
    element.style.display = "";
    element.style.transition = `opacity ${duration}ms ease`;
    requestAnimationFrame(() => {
        element.style.opacity = "1";
    });
}

function fadeOut(element: HTMLElement, duration: number, onComplete: () => void): void {
    element.style.transition = `opacity ${duration}ms ease`;
    element.style.opacity = "0";
    setTimeout(onComplete, duration);
}

function notify(
    type: string,
    message?: string,
    title?: string,
    optionsOverride?: Partial<ToastrOptions>,
): HTMLElement | null {
    const options: ToastrOptions = { ...getOptions(), ...optionsOverride };
    const iconClass = optionsOverride?.iconClass ?? options.iconClasses[type] ?? options.iconClass;

    if (options.preventDuplicates && message === previousToast) {
        return null;
    }
    if (message) {
        previousToast = message;
    }

    container = getContainer(options, true);
    if (!container) return null;

    const toastElement = document.createElement("div");
    toastElement.tabIndex = 0;
    toastElement.className = `${options.toastClass} ${iconClass}`;
    if (options.rtl) toastElement.classList.add("rtl");

    if (title) {
        const titleElement = document.createElement("div");
        titleElement.className = options.titleClass;
        titleElement.innerHTML = options.escapeHtml ? escapeHtmlString(title) : title;
        toastElement.appendChild(titleElement);
    }

    if (message) {
        const messageElement = document.createElement("div");
        messageElement.className = options.messageClass;
        messageElement.innerHTML = options.escapeHtml ? escapeHtmlString(message) : message;
        toastElement.appendChild(messageElement);
    }

    if (options.closeButton) {
        const closeWrapper = document.createElement("span");
        closeWrapper.innerHTML = options.closeHtml;
        const closeElement = closeWrapper.firstElementChild as HTMLElement;
        if (closeElement) {
            closeElement.className = options.closeClass;
            toastElement.appendChild(closeElement);
        }
    }

    let progressElement: HTMLElement | null = null;
    if (options.progressBar) {
        progressElement = document.createElement("div");
        progressElement.className = options.progressClass;
        toastElement.prepend(progressElement);
    }

    if (options.newestOnTop) {
        container.prepend(toastElement);
    } else {
        container.appendChild(toastElement);
    }

    fadeIn(toastElement, options.showDuration);

    let hideTimeout: ReturnType<typeof setTimeout> | null = null;
    let progressInterval: ReturnType<typeof setInterval> | null = null;
    let hideEta = 0;
    let maxHideTime = 0;

    const hideToast = (force = false) => {
        if (!force && document.activeElement === toastElement) return;
        clearTimers();
        fadeOut(toastElement, options.hideDuration, () => removeToast(toastElement));
    };

    const startTimer = (duration: number) => {
        if (duration <= 0) return;
        maxHideTime = duration;
        hideEta = Date.now() + duration;
        hideTimeout = setTimeout(hideToast, duration);
        if (options.progressBar && progressElement) {
            progressInterval = setInterval(() => {
                const pct = ((hideEta - Date.now()) / maxHideTime) * 100;
                if (progressElement) progressElement.style.width = `${Math.max(pct, 0)}%`;
            }, 10);
        }
    };

    const clearTimers = () => {
        if (hideTimeout) clearTimeout(hideTimeout);
        if (progressInterval) clearInterval(progressInterval);
        hideTimeout = null;
        progressInterval = null;
    };

    if (options.closeOnHover) {
        toastElement.addEventListener("mouseenter", () => {
            clearTimers();
            if (progressElement) progressElement.style.width = "100%";
        });
        toastElement.addEventListener("mouseleave", () => {
            if (options.extendedTimeOut > 0) startTimer(options.extendedTimeOut);
        });
        toastElement.addEventListener("focusin", () => clearTimers());
        toastElement.addEventListener("focusout", () => {
            if (options.extendedTimeOut > 0) startTimer(options.extendedTimeOut);
        });
    }

    if (!options.onclick && options.tapToDismiss) {
        toastElement.addEventListener("click", () => hideToast(true));
        toastElement.addEventListener("keyup", (event) => {
            if (event.key === "Enter") {
                event.preventDefault();
                hideToast(true);
            }
        });
    }

    if (options.onclick) {
        toastElement.addEventListener("click", (event) => {
            options.onclick?.(event);
            hideToast(true);
        });
    }

    if (options.timeOut > 0) {
        startTimer(options.timeOut);
    }

    return toastElement;
}

function clear(toastElement?: HTMLElement | null): void {
    const options = getOptions();
    if (!container) getContainer(options);
    if (toastElement) {
        fadeOut(toastElement, options.hideDuration, () => removeToast(toastElement));
        return;
    }
    if (container) {
        Array.from(container.children).forEach((child) => {
            fadeOut(child as HTMLElement, options.hideDuration, () => removeToast(child as HTMLElement));
        });
    }
}

const toastr = {
    clear,
    error(message?: string, title?: string, optionsOverride?: Partial<ToastrOptions>) {
        return notify("error", message, title, optionsOverride);
    },
    getContainer,
    info(message?: string, title?: string, optionsOverride?: Partial<ToastrOptions>) {
        return notify("info", message, title, optionsOverride);
    },
    options: {} as Partial<ToastrOptions>,
    success(message?: string, title?: string, optionsOverride?: Partial<ToastrOptions>) {
        return notify("success", message, title, optionsOverride);
    },
    warning(message?: string, title?: string, optionsOverride?: Partial<ToastrOptions>) {
        return notify("warning", message, title, optionsOverride);
    },
};

export { toastr };
export default toastr;

